import { File, FileSpreadsheet, FileText, type LucideIcon } from 'lucide-react';
import { isImageEntry, isPdfEntry, jobFileExtension } from '@jobos/shared';
import type { DisplayFile, JobFileAttachment, JobFileEntry } from '@jobos/shared';

// `isImageEntry`/`isPdfEntry` now live in shared — the backend asks the same
// "is this a photo" question for the job list's photo count. Re-exported here
// so this module stays the one import site every consumer already uses for
// display helpers, rather than half of them reaching into `@jobos/shared`
// directly and half reaching in here.
export { isImageEntry, isPdfEntry };

/**
 * Whether there is a raster preview to draw on a card — the question the
 * thumbnail asks, as opposed to `isImageEntry`'s "is this a photo".
 *
 * A PDF with a backfilled first-page render answers yes to this and no to that,
 * and showing that render is better than a generic document icon.
 */
export function hasThumbnail(file: DisplayFile): boolean {
  return file.thumbKey != null || isImageEntry(file);
}

/** What the viewer can show for a file: a picture, a PDF, or nothing at all. */
export type JobFilePreviewKind = 'image' | 'pdf' | 'none';

/**
 * How `View` on a file should be answered.
 *
 * One place decides, so the grid, the list and the section can't drift into
 * disagreeing about what a file is. Image wins first: a backfilled photo can
 * carry no content type, and reading one as a document would send it to the
 * wrong viewer. `isImageEntry` excludes PDFs, so that order is safe for them.
 */
export function jobFilePreviewKind(file: DisplayFile): JobFilePreviewKind {
  if (isImageEntry(file)) return 'image';
  if (isPdfEntry(file)) return 'pdf';
  return 'none';
}

const SPREADSHEET_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const SPREADSHEET_EXTENSIONS = ['.xls', '.xlsx', '.csv'];
const DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

/**
 * The icon a non-image file gets.
 *
 * Content type first, extension second: an upload that arrived without a usable
 * type still has a name to go on.
 */
export function iconForJobFile(file: DisplayFile): LucideIcon {
  const type = (file.contentType ?? '').toLowerCase();
  if (SPREADSHEET_TYPES.includes(type)) return FileSpreadsheet;
  if (DOCUMENT_TYPES.includes(type)) return FileText;

  const ext = jobFileExtension(file.fileName);
  if (SPREADSHEET_EXTENSIONS.includes(ext)) return FileSpreadsheet;
  if (DOCUMENT_EXTENSIONS.includes(ext)) return FileText;
  return File;
}

/**
 * How each derived attachment state reads on a card.
 *
 * `attached` gets nothing: a receipt doing its job is the ordinary case, and a
 * badge on every one of them would say nothing while burying the two that do.
 */
export const ATTACHMENT_BADGES: Record<JobFileAttachment, { label: string; className: string } | null> = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700' },
  attached: null,
  unattached: { label: 'Unattached', className: 'bg-gray-100 text-gray-600' },
};

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

/**
 * What a file weighs, as the list view's size column shows it.
 *
 * Reports the original where one landed, because Download is what the number
 * sets an expectation for and Download serves the original — the display
 * rendition a photo stores in `sizeBytes` is a fraction of it. A backfilled row
 * may store neither, hence the em-dash.
 */
export function formatJobFileSize(
  file: Pick<JobFileEntry, 'sizeBytes' | 'originalSizeBytes'>,
): string {
  const bytes = file.originalSizeBytes ?? file.sizeBytes;
  if (bytes == null) return '—';

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal, but only when it says something: "1.2 MB" earns it, "4.0 MB"
  // does not.
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  return `${rounded} ${SIZE_UNITS[unit]}`;
}

/** The bytes a size sort orders by — null for the entries that store none. */
export function jobFileSortSize(
  file: Pick<JobFileEntry, 'sizeBytes' | 'originalSizeBytes'>,
): number | null {
  return file.originalSizeBytes ?? file.sizeBytes;
}
